import React from 'react';
import { NavLink, Link } from 'react-router-dom';
import { Home, Sparkles, Library, Compass, Plus, Crown, LogIn, LogOut, Settings as SettingsIcon, User } from 'lucide-react';
import Logo from './Logo';
import { useApp } from '../context/AppContext';
import { Button } from './ui/button';
import { PLAYLISTS } from '../mock/mock';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';

const NavItem = ({ to, icon: Icon, label, end }) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
        isActive ? 'bg-white/10 text-white' : 'text-zinc-400 hover:text-white hover:bg-white/5'
      }`
    }
  >
    <Icon size={18} />
    <span>{label}</span>
  </NavLink>
);

export default function Sidebar() {
  const { t, user, lang, signOut } = useApp();
  return (
    <aside className="hidden lg:flex flex-col w-64 shrink-0 h-screen sticky top-0 border-r border-white/5 bg-[#0d0d0f]">
      <div className="px-5 py-4">
        <Link to="/"><Logo /></Link>
      </div>
      <div className="px-3">
        <Link to="/create">
          <Button className="w-full justify-start gap-2 bg-white text-black hover:bg-zinc-200 rounded-lg h-10">
            <Plus size={18} /> {t('nav.create')}
          </Button>
        </Link>
      </div>
      <nav className="px-3 py-4 flex flex-col gap-1">
        <NavItem to="/" end icon={Home} label={t('nav.home')} />
        <NavItem to="/create" icon={Sparkles} label={t('nav.create')} />
        <NavItem to="/library" icon={Library} label={t('nav.library')} />
        <NavItem to="/explore" icon={Compass} label={t('nav.explore')} />
      </nav>

      <div className="px-5 mt-2 mb-2 text-[11px] uppercase tracking-wider text-zinc-500">Playlists</div>
      <div className="px-3 flex-1 overflow-y-auto space-y-1 pb-4">
        {PLAYLISTS.map(p => (
          <div key={p.id} className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-white/5 cursor-pointer">
            <img src={p.cover} alt="" className="w-9 h-9 rounded-md object-cover" />
            <div className="min-w-0">
              <div className="text-sm truncate">{p.title[lang] || p.title.en}</div>
              <div className="text-xs text-zinc-500">{p.count} {lang==='fr'?'titres':'tracks'}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="p-3 border-t border-white/5">
        {user ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/5">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-500 via-orange-400 to-yellow-300 flex items-center justify-center text-black font-bold">
                  {(user.name || user.email || 'U')[0].toUpperCase()}
                </div>
                <div className="min-w-0 text-left">
                  <div className="text-sm truncate">{user.name || user.email}</div>
                  <div className="text-xs text-zinc-500">{user.credits} {t('nav.credits')}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-[#141416] border-white/10 text-zinc-200 w-56">
              <DropdownMenuItem asChild><Link to={`/u/${user.id}`}><User size={14} className="mr-2" /> {t('nav.profile')}</Link></DropdownMenuItem>
              <DropdownMenuItem asChild><Link to="/settings"><SettingsIcon size={14} className="mr-2" /> {t('nav.settings')}</Link></DropdownMenuItem>
              <DropdownMenuItem asChild><Link to="/library">{t('nav.library')}</Link></DropdownMenuItem>
              <DropdownMenuItem onClick={signOut} className="text-red-400 focus:text-red-300">
                <LogOut size={14} className="mr-2" /> {t('nav.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <div className="space-y-2">
            <Link to="/auth?mode=signup"><Button className="w-full bg-white text-black hover:bg-zinc-200">{t('nav.signup')}</Button></Link>
            <Link to="/auth?mode=signin"><Button variant="outline" className="w-full border-white/15 bg-transparent hover:bg-white/5"><LogIn size={16} className="mr-2" />{t('nav.signin')}</Button></Link>
          </div>
        )}
        <Link to="/settings" className="mt-2 flex items-center gap-2 text-xs text-zinc-400 hover:text-white px-2 py-1.5">
          <Crown size={14} className="text-yellow-400" /> {t('nav.upgrade')}
        </Link>
      </div>
    </aside>
  );
}
